import React, { useEffect, useRef, useState } from "react";
import logoimg from "../../assets/logo.png.png";
import { useAuth } from "../../contexts/AuthContext";
import { getProfileDetails } from "../../api/profile";

interface HeaderProps {
  onProfileClick: () => void;
  onNotificationClick: () => void;
}

export default function Header({ onProfileClick, onNotificationClick }: HeaderProps) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const displayName = user?.displayName || user?.email?.split("@")[0] || "User";
  const displayEmail = user?.email || "—";

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!open) return;
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useEffect(() => {
    let alive = true;

    const loadAvatar = async () => {
      try {
        const data = await getProfileDetails();
        if (!alive) return;
        const url =
          (data as any)?.profile_photo ||
          (data as any)?.profile?.profile_photo ||
          null;
        setAvatarUrl(url || null);
      } catch {
        if (!alive) return;
        setAvatarUrl(null);
      }
    };

    loadAvatar();
    

    const handler = () => loadAvatar();
    window.addEventListener("roott:profile-photo-updated", handler as EventListener);

    return () => {
      alive = false;
      window.removeEventListener("roott:profile-photo-updated", handler as EventListener);
    };
  }, []);


  return (
    <header className="sticky top-0 z-30 bg-white border-b">
      <div className="mx-auto px-4 py-3 flex items-center gap-4">
        <img src={logoimg} alt="Logo" className="h-8 w-auto" />

        <div className="ml-auto flex items-center gap-4">
          <button onClick={onNotificationClick} aria-label="Notifications">
            <NotificationIcon />
          </button>

          <div className="relative">
            <button
              ref={btnRef}
              onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-3"
              aria-haspopup="menu"
              aria-expanded={open}
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName}
                  className="h-[40px] w-[40px] rounded-full border border-black object-cover"
                />
              ) : (
                <UserIcon />
              )}

              <div className="hidden sm:block leading-tight text-left">
                <div className="text-base">{displayName}</div>
                <div className="text-xs text-[#777B7F]">{displayEmail}</div>
              </div>
              <ChevronDown />
            </button>

            {open && (
              <div
                ref={menuRef}
                role="menu"
                className="absolute right-0 mt-2 w-56 rounded-lg border bg-white shadow-lg z-40 py-2"
              >
                <button
                  role="menuitem"
                  className="w-full px-4 py-2 text-left hover:bg-gray-50"
                  onClick={() => {
                    setOpen(false);
                    onProfileClick();
                  }}
                >
                  Profile
                </button>
                <button
                  role="menuitem"
                  className="w-full px-4 py-2 text-left hover:bg-gray-50"
                  onClick={() => {
                    setOpen(false);
                    onNotificationClick();
                  }}
                >
                  Notifications
                </button>
                <div className="my-2 border-t" />
                <button
                  role="menuitem"
                  className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50"
                  onClick={() => {
  setOpen(false);
  setShowLogoutModal(true);
}}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {showLogoutModal && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
      <h2 className="text-lg font-semibold mb-3 text-gray-800">
        Confirm Logout
      </h2>

      <p className="text-sm text-gray-600 mb-6">
        Are you sure you want to log out of your account?
      </p>

      <div className="flex justify-end gap-3">
        <button
          onClick={() => setShowLogoutModal(false)}
          className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-sm"
        >
          Cancel
        </button>

        <button
          onClick={async () => {
            setShowLogoutModal(false);
            await logout();
          }}
          className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm"
        >
          Logout
        </button>
      </div>
    </div>
  </div>
)}
    </header>

    
  );
}

const SearchIcon = () => <span className="material-icons text-md">search</span>;
const ChevronDown = () => <span className="material-icons text-md">keyboard_arrow_down</span>;
const NotificationIcon = () => (
  <span className="material-icons text-md bg-black rounded-full text-primary h-[40px] w-[40px] flex items-center justify-center">
    notifications
  </span>
);
const UserIcon = () => (
  <span className="material-icons text-md bg-white border border-black rounded-full text-black h-[40px] w-[40px] flex items-center justify-center">
    person
  </span>
);
